import type { ReactNode } from 'react';
import { useCallback, useEffect } from 'react';

import type { IconList } from '@snowit/assets';

import { useStore } from '@snowit/store';

import WebFont from 'webfontloader';

const FONT_FAMILY = 'Rubik';
const env = process.env.SNOWIT_ENVIRONMENT || 'development';
const logo: IconList = env === 'development' ? 'sportit-admin-dev' : 'sportit-admin';

const assignCssVar = (name: string, value: string | null): void => {
  if (typeof value === 'undefined') return;
  document.documentElement.style.setProperty(`--${name}`, value);
};

const changePalette = (input: Record<string, string>) => {
  for (const [key, value] of Object.entries(input)) {
    assignCssVar(key, value);
  }
};

export const Provider = ({ children }: { children: ReactNode }) => {
  const settings = useStore((state) => state.store.settings);

  useEffect(() => {
    document.body.setAttribute('data-env', `panel-${env}`);
    WebFont.load({
      google: {
        families: [`${FONT_FAMILY}:300,400,500,700`],
      },
    });
  }, []);

  useEffect(() => {
    document.body.setAttribute('data-theme', settings.theme);
    assignCssVar('fs-default', settings.fontsize);
  }, [settings.fontsize, settings.theme]);

  return children;
};

export default () => {
  const settings = useStore((state) => state.store.settings);
  const setStore = useStore((state) => state.setStore);

  const changeTheme = useCallback(
    (theme: string) => {
      setStore('settings', { theme });
      localStorage.setItem('theme', theme);
    },
    [setStore],
  );

  const changeFontSize = useCallback(
    (fontsize: string) => {
      setStore('settings', { fontsize });
      localStorage.setItem('fontsize', fontsize);
    },
    [setStore],
  );

  return {
    assignCssVar,
    theme: settings.theme,
    changeTheme,
    changeFontSize,
    changePalette,
    logo,
    env,
    fontsize: settings.fontsize,
  };
};
